import { useState } from "react";
import { Button } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { setAuthToken } from "../utils/tokenHeaders";
import { useSelector } from "react-redux";
import AppPageShell from "./AppPageShell";
import { PRICING_FAQ, PRICING_SECTION } from "../content/pricing";
import { formatSubscriptionExpiry } from "../utils/userProfile";

const SUPPORT_EMAIL = "support@club-lab.ru";

const prodURL = "/api";
const url =
  window.location.hostname === "localhost"
    ? "http://localhost:3004/api"
    : prodURL;

function formatPrice(value) {
  return new Intl.NumberFormat("ru-RU").format(value);
}

function PricingCard({ plan, onBuy, disabled }) {
  const isContactOnly = plan.contactOnly;

  return (
    <div
      className={`app-price-card${
        plan.highlight ? " app-price-card--featured" : ""
      }${isContactOnly ? " app-price-card--contact" : ""}`}
    >
      {plan.highlight ? (
        <span className="app-price-card__tag">Популярный</span>
      ) : null}
      <div className="app-price-card__title">{plan.title}</div>
      {plan.segmentLabel ? (
        <div className="app-price-card__segment-inline">{plan.segmentLabel}</div>
      ) : null}
      <div className="app-price-card__badge">{plan.badge}</div>
      {!isContactOnly ? (
        <div className="app-price-card__price">
          {formatPrice(plan.price)} &#8381;
          {plan.pricePeriod ? (
            <span className="app-price-card__period">{plan.pricePeriod}</span>
          ) : null}
        </div>
      ) : (
        <div className="app-price-card__price app-price-card__price--contact">
          По запросу
        </div>
      )}
      <div className="app-price-card__per-unit">{plan.perUnit}</div>
      <ul className="app-price-card__features">
        {plan.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      {isContactOnly ? (
        <Button
          variant="outlined"
          className="app-btn app-price-card__cta"
          component="a"
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
            "Групповой доступ 100+ сотрудников — MOGAME"
          )}`}
        >
          Написать нам
        </Button>
      ) : (
        <Button
          variant="contained"
          className="app-btn app-btn--primary app-price-card__cta"
          onClick={() => onBuy(plan.packageId)}
          disabled={disabled}
        >
          Оформить
        </Button>
      )}
    </div>
  );
}

function formatBusinessPlanLabel(subscription) {
  if (!subscription?.label) return null;
  const max = subscription.teamSizeMax;
  return max ? `${subscription.label} (до ${max} чел.)` : subscription.label;
}

export default function Price() {
  const user = useSelector((state) => state.user?.data);
  const profile = useSelector((state) => state.user?.profile);
  const [faqOpen, setFaqOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState(null);

  const businessSubscription =
    profile?.businessSubscription ?? user?.user?.businessSubscription ?? null;
  const businessUntil = formatSubscriptionExpiry(businessSubscription);
  const businessPlan = formatBusinessPlanLabel(businessSubscription);

  const handlePayment = async (packageId) => {
    setError(null);
    setLoadingId(packageId);

    try {
      const response = await fetch(`${url}/payment/create-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: setAuthToken(),
        },
        body: JSON.stringify({
          id: user?.user?.id,
          packageId,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.type === "error") {
        setError(result.message || "Не удалось создать платёж");
        return;
      }

      if (result.confirmation?.confirmation_url) {
        window.open(result.confirmation.confirmation_url, "_blank");
      }
    } catch {
      setError("Не удалось создать платёж. Попробуйте позже.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <AppPageShell
      className="app-page--pricing"
      title="Тарифы"
      subtitle="Корпоративное обучение в Telegram или MAX — подписка по размеру команды"
    >
      <div className="app-price-body app-price-body--business">
        <div className="app-price-balance app-price-balance--compact app-price-balance--single">
          <div className="app-price-balance__item">
            <span className="app-price-balance__label">Групповой доступ</span>
            <span className="app-price-balance__value app-price-balance__value--text">
              {businessUntil
                ? `${businessPlan ?? "Активен"} до ${businessUntil}`
                : "Не подключён"}
            </span>
          </div>
        </div>

        <div className="app-price-packages">
          <div className="app-price-step app-price-step--packages">
            <h2 className="app-price-step__title">{PRICING_SECTION.title}</h2>
            <p className="app-price-step__desc">{PRICING_SECTION.subtitle}</p>
          </div>

          {error ? <div className="app-price-error">{error}</div> : null}

          <div className="app-price-grid app-price-grid--three">
            {PRICING_SECTION.plans.map((plan) => (
              <PricingCard
                key={plan.packageId}
                plan={plan}
                onBuy={handlePayment}
                disabled={loadingId === plan.packageId}
              />
            ))}
          </div>
        </div>

        <div className="app-price-help-row">
          <span className="app-price-help-row__text">Нужна помощь с выбором тарифа?</span>
          <button
            type="button"
            className={`app-price-help-btn${
              faqOpen ? " app-price-help-btn--active" : ""
            }`}
            onClick={() => setFaqOpen((open) => !open)}
            aria-expanded={faqOpen}
            aria-label="Частые вопросы"
            title="Частые вопросы"
          >
            <HelpOutlineIcon fontSize="small" />
          </button>
        </div>

        {faqOpen ? (
          <section className="app-price-faq app-price-faq--inline">
            <h2 className="app-price-faq__title">Частые вопросы</h2>
            <div className="app-price-faq__list">
              {PRICING_FAQ.map((item) => (
                <details className="app-price-faq__item" key={item.question}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <div className="app-price-note">
        <div>
          Оплата через{" "}
          <a href="https://yoomoney.ru" target="_blank" rel="noreferrer">
            ЮMoney
          </a>
          . Подписка активируется на 30 дней.
        </div>
        <div>Поддержка: support@club-lab.ru</div>
      </div>
    </AppPageShell>
  );
}
